import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import _noop from 'lodash/noop';
import * as Yup from 'yup';

import { Icons, MaskedInput as Input } from '@casanova/casanova-common';
import { ActionModal } from '@casanova/casanova-common/lib/Modal';

import i18n from '@i18n';
import { FormModal } from 'components';
import { alphaEmailMask } from 'utils/masks';
import ResponseDialog from '../../../../components/ResponseDialog';
import { resetPromissoryNoteState } from 'store/dailyRentContracts/actions';
import { set } from 'store';
import { sendCancelledPromissoryNoteSucceed } from '../../../../store/dailyRentContracts/actions';

// Esquema de validación
const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Por favor, ingresa un email válido.')
    .required('El email es requerido.'),
});

export default function CancelPromissoryNote({
  show = false,
  contractUuid = '',
  onCancel = _noop,
  cancelPromissoryNote = _noop,
  sendCancelledPromissoryNote = _noop,
  sendPromissoryNoteState = {},
  cancelPromissoryNoteState = {},
  getDailyRentContractsList = _noop,
  params = {},
  filters = {},
  searchPattern = {},
}) {
  const [email, setEmail] = useState('');
  const [modalState, setModalState] = useState('main');

  const dispatch = useDispatch();
  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value);
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: (values) => {
      sendCancelledPromissoryNote({
        contract: contractUuid,
        email: values.email,
      });
      setModalState('loading');
    },
  });

  const resetState = () => {
    setEmail('');
    dispatch(resetPromissoryNoteState());
  };

  const handleCancel = useCallback(() => {
    formik.resetForm();
    onCancel();
    setModalState('main');
    resetState();
    getDailyRentContractsList({ params, filters, searchPattern });
  }, [onCancel]);

  const handleConfirmCancelPromissoryNote = () => {
    cancelPromissoryNote(contractUuid);
    setModalState('loading');
  };

  const handleResponseDialogClose = () => {
    if (cancelPromissoryNoteState.success) {
      setModalState('sendEmail'); 
    } else {
      handleCancel();
    }
  };

  const handleRetrySendPromissoryNote = () => {
    resetState();
    setModalState('sendEmail');
  };

  if (!show) {
    return null;
  }

  return (
    <>
      {modalState === 'main' && (
        <ActionModal
          open={show}
          icon={<Icons.StatusWarning />}
          onClose={handleCancel}
          onAction={handleConfirmCancelPromissoryNote}
          title="CANCELAR PAGARE" // mandar al i18n
          message="Se cancelará el pagaré, ¿Estas seguro que quieres continuar?"
          actionLabel={i18n('COMMONS__YES_CONTINUE__TEXT')}
          closeButton
        />
      )}

      {modalState === 'sendEmail' && (
        <FormModal
          open
          title={i18n('SEND_PROMISSORY_NOTE__DIALOG__TITLE')}
          description={i18n('SEND_PROMISSORY_NOTE_DIALOG_TEXT')}
          submitLabel={i18n('COMMONS__SEND__TEXT')}
          icon={<Icons.IcoCarOk />}
          onCancel={handleCancel}
          onSubmit={formik.handleSubmit}
        >
          <div className="row">
            <div className="col-12">
              <Input
                name="email"
                placeholder={i18n('COMMONS__PLACEHOLDER__WRITE')}
                maskPlaceholder=""
                value={formik.values.email}
                label="Email*"
                mask={alphaEmailMask(50)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                classNameError={formik.errors.email ? 'is-invalid' : ''}
              />
              {formik.touched.email && formik.errors.email && (
                <span style={{ color: 'red' }}>{formik.errors.email}</span>
              )}
            </div>
          </div>
        </FormModal>
      )}

      <ResponseDialog
        open={cancelPromissoryNoteState.open}
        success={cancelPromissoryNoteState.success}
        errorCode={cancelPromissoryNoteState.errorCode}
        errorTitle={i18n('CANCELLED_PROMISSORY_NOTE_ERROR__DIALOG__TITLE')}
        errorMessage={cancelPromissoryNoteState.errorMessage}
        successTitle={i18n('CANCELLED_PROMISSORY_NOTE__DIALOG__TITLE')}
        successLabel="Continuar"
        successMessage={i18n('CANCELLED_PROMISSORY_NOTE_DIALOG_TEXT')}
        onSuccess={() => {
          setModalState('sendEmail');
        }}
        onError={handleResponseDialogClose}
        onClose={handleResponseDialogClose}
        errorLabel={i18n('COMMONS__CLOSE__TEXT')}
      />

      <ResponseDialog
        open={sendPromissoryNoteState.open}
        success={sendPromissoryNoteState.success}
        errorCode={sendPromissoryNoteState.errorCode}
        errorMessage={sendPromissoryNoteState.errorMessage}
        successTitle="Se ha enviado el pagaré"
        successLabel="Continuar"
        successMessage="El pagaré cancelado ha sido enviado correctamente"
        onSuccess={handleCancel}
        onError={handleRetrySendPromissoryNote}
        onClose={handleCancel}
      />
    </>
  );
}

CancelPromissoryNote.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  cancelPromissoryNote: PropTypes.func,
  sendCancelledPromissoryNote: PropTypes.func,
  sendPromissoryNoteState: PropTypes.object,
  cancelPromissoryNoteState: PropTypes.object,
};

CancelPromissoryNote.defaultProps = {
  onCancel: _noop,
  cancelPromissoryNote: _noop,
  sendCancelledPromissoryNote: _noop,
  sendPromissoryNoteState: {},
  cancelPromissoryNoteState: {},
};
